import React, { useState, useEffect, useRef } from 'react';
import { 
  UserPlus, 
  Upload, 
  CheckCircle2, 
  FileText, 
  Camera, 
  CreditCard, 
  Award, 
  IdCard, 
  Trash2, 
  Eye, 
  RefreshCw, 
  AlertCircle,
  File
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';

const DOCUMENT_TYPES = [
  {
    key: 'personal_photo',
    title: 'الصورة الشخصية',
    subtitle: 'صورة شخصية حديثة بخلفية بيضاء',
    accept: 'image/jpeg,image/png,image/webp',
    acceptText: 'JPG, PNG, WEBP (حتى 15MB)',
    icon: Camera,
    color: '#0F388E'
  },
  {
    key: 'high_school_cert',
    title: 'استمارة / شهادة الثانوية',
    subtitle: 'أصل أو صورة واضحة لاستمارة الثانوية',
    accept: 'image/jpeg,image/png,image/webp,application/pdf',
    acceptText: 'PDF, JPG, PNG (حتى 15MB)',
    icon: Award,
    color: '#C9A040'
  },
  {
    key: 'seating_number_card',
    title: 'بطاقة رقم الجلوس',
    subtitle: 'كرت أو إشعار رقم الجلوس للامتحانات',
    accept: 'image/jpeg,image/png,image/webp,application/pdf',
    acceptText: 'PDF, JPG, PNG (حتى 15MB)',
    icon: CreditCard,
    color: '#0F388E'
  },
  {
    key: 'national_id_card',
    title: 'البطاقة الشخصية أو جواز السفر',
    subtitle: 'صورة الهوية الوطنية أو جواز السفر',
    accept: 'image/jpeg,image/png,image/webp,application/pdf',
    acceptText: 'PDF, JPG, PNG (حتى 15MB)',
    icon: IdCard,
    color: '#C9A040'
  }
];

const initialDocsState = {
  personal_photo: { url: '', name: '', size: '', progress: 0, uploading: false, success: false, error: null, preview: '' },
  high_school_cert: { url: '', name: '', size: '', progress: 0, uploading: false, success: false, error: null, preview: '' },
  seating_number_card: { url: '', name: '', size: '', progress: 0, uploading: false, success: false, error: null, preview: '' },
  national_id_card: { url: '', name: '', size: '', progress: 0, uploading: false, success: false, error: null, preview: '' },
};

const Register = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [programs, setPrograms] = useState([]);
  const [colleges, setColleges] = useState([]);
  const [documents, setDocuments] = useState(initialDocsState);
  const [dragOverKey, setDragOverKey] = useState(null);
  const fileInputRefs = useRef({});

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const handleFileUpload = (key, file) => {
    if (!file) return;

    // Validate size (15MB)
    if (file.size > 15 * 1024 * 1024) {
      toast({
        title: '⚠️ حجم الملف كبير جداً',
        description: 'الحد الأقصى لحجم الملف هو 15 ميجابايت',
        variant: 'destructive'
      });
      return;
    }

    // Validate extension
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    const allowedExts = ['.jpg', '.jpeg', '.png', '.webp', '.pdf'];
    if (!allowedExts.includes(ext)) {
      toast({
        title: '⚠️ صيغة الملف غير مدعومة',
        description: 'الصيغ المسموحة هي: JPG, PNG, WEBP, PDF فقط',
        variant: 'destructive'
      });
      return;
    }

    // Create local object URL for instant image preview
    let localPreview = '';
    if (file.type.startsWith('image/')) {
      try {
        localPreview = URL.createObjectURL(file);
      } catch (e) {}
    }

    // Set uploading state
    setDocuments(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        name: file.name,
        size: formatFileSize(file.size),
        progress: 10,
        uploading: true,
        success: false,
        error: null,
        preview: localPreview
      }
    }));

    const uploadFormData = new FormData();
    uploadFormData.append('file', file);

    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${BACKEND_URL}/api/registrations/upload`);

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        const percent = Math.min(95, Math.round((event.loaded / event.total) * 100));
        setDocuments(prev => ({
          ...prev,
          [key]: { ...prev[key], progress: percent }
        }));
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const data = JSON.parse(xhr.responseText);
          const uploadedUrl = data.url || data.file_path;
          setDocuments(prev => ({
            ...prev,
            [key]: {
              url: uploadedUrl,
              name: data.original_name || file.name,
              size: formatFileSize(file.size),
              progress: 100,
              uploading: false,
              success: true,
              error: null,
              preview: localPreview || (file.type.startsWith('image/') ? `${BACKEND_URL}${uploadedUrl}` : '')
            }
          }));

          const docTitle = DOCUMENT_TYPES.find(d => d.key === key)?.title || 'المستند';
          toast({
            title: '✅ تم الرفع بنجاح',
            description: `تم رفع ${docTitle} بنجاح`
          });
        } catch (err) {
          handleUploadError(key, 'خطأ في معالجة استجابة الخادم');
        }
      } else {
        let msg = 'فشل رفع الملف';
        try {
          const res = JSON.parse(xhr.responseText);
          if (res.detail) msg = res.detail;
        } catch (e) {}
        handleUploadError(key, msg);
      }
    };

    xhr.onerror = () => {
      handleUploadError(key, 'فشل الاتصال بالخادم أثناء رفع الملف');
    };

    xhr.send(uploadFormData);
  };

  const handleUploadError = (key, errorMsg) => {
    setDocuments(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        uploading: false,
        success: false,
        error: errorMsg,
        progress: 0
      }
    }));
    toast({
      title: '❌ خطأ في الرفع',
      description: errorMsg,
      variant: 'destructive'
    });
  };

  const handleRemoveFile = (key, e) => {
    if (e) e.stopPropagation();
    if (fileInputRefs.current[key]) {
      fileInputRefs.current[key].value = '';
    }
    setDocuments(prev => ({
      ...prev,
      [key]: {
        url: '',
        name: '',
        size: '',
        progress: 0,
        uploading: false,
        success: false,
        error: null,
        preview: ''
      }
    }));
  };

  const triggerFileInput = (key) => {
    if (fileInputRefs.current[key]) {
      fileInputRefs.current[key].click();
    }
  };

  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    gender: 'ذكر',
    birth_date: '',
    nationality: 'يمني',
    city: '',
    address: '',
    qualification: 'الثانوية العامة',
    graduation_year: '',
    gpa: '',
    school_name: '',
    desired_program: '',
    desired_college: '',
    guardian_name: '',
    guardian_phone: '',
    guardian_relation: 'والد'
  });

  const [allPrograms, setAllPrograms] = useState([]); // All programs
  const [filteredPrograms, setFilteredPrograms] = useState([]); // Filtered by college

  useEffect(() => {
    fetchPrograms();
    fetchColleges();
  }, []);

  // Filter programs when college changes
  useEffect(() => {
    if (formData.desired_college && allPrograms.length > 0) {
      // Find the selected college
      const selectedCollege = colleges.find(c => c.name_ar === formData.desired_college);
      if (selectedCollege) {
        // Filter programs by college_id
        const filtered = allPrograms.filter(p => p.college_id === selectedCollege.id);
        setFilteredPrograms(filtered);
        // Reset program selection if current program is not in filtered list
        if (formData.desired_program && !filtered.some(p => p.name_ar === formData.desired_program)) {
          setFormData(prev => ({ ...prev, desired_program: '' }));
        }
      } else {
        setFilteredPrograms([]);
      }
    } else {
      setFilteredPrograms([]);
      setFormData(prev => ({ ...prev, desired_program: '' }));
    }
  }, [formData.desired_college, allPrograms, colleges]);

  const fetchPrograms = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/programs/`);
      const data = await response.json();
      setAllPrograms(data);
    } catch (error) {
      console.error('Error fetching programs:', error);
    }
  };

  const fetchColleges = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/colleges/`);
      const data = await response.json();
      setColleges(data);
    } catch (error) {
      console.error('Error fetching colleges:', error);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSelectChange = (name, value) => {
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check if any document is currently in progress of uploading
    const isUploadingAny = Object.values(documents).some(d => d.uploading);
    if (isUploadingAny) {
      toast({
        title: '⏳ يرجى الانتظار',
        description: 'جاري رفع المستندات، يرجى الانتظار حتى يكتمل شريط التحميل',
      });
      return;
    }

    setLoading(true);

    const payload = {
      ...formData,
      personal_photo: documents.personal_photo.url || null,
      high_school_cert: documents.high_school_cert.url || null,
      seating_number_card: documents.seating_number_card.url || null,
      national_id_card: documents.national_id_card.url || null,
      documents: {
        personal_photo: documents.personal_photo.url || null,
        high_school_cert: documents.high_school_cert.url || null,
        seating_number_card: documents.seating_number_card.url || null,
        national_id_card: documents.national_id_card.url || null,
      }
    };

    try {
      const response = await fetch(`${BACKEND_URL}/api/registrations/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        toast({
          title: '✅ تم إرسال طلب التسجيل بنجاح',
          description: 'سنتواصل معك قريباً لاستكمال إجراءات التسجيل والمطابقة',
        });
        
        // Reset form and documents
        setFormData({
          full_name: '',
          email: '',
          phone: '',
          gender: 'ذكر',
          birth_date: '',
          nationality: 'يمني',
          city: '',
          address: '',
          qualification: 'الثانوية العامة',
          graduation_year: '',
          gpa: '',
          school_name: '',
          desired_program: '',
          desired_college: '',
          guardian_name: '',
          guardian_phone: '',
          guardian_relation: 'والد'
        });
        setDocuments(initialDocsState);
      } else {
        const error = await response.json();
        toast({
          title: '❌ فشل إرسال الطلب',
          description: error.detail || 'حدث خطأ أثناء إرسال الطلب',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Error submitting registration:', error);
      toast({
        title: '❌ خطأ في الإرسال',
        description: 'حدث خطأ أثناء إرسال الطلب. يرجى المحاولة مرة أخرى',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-[#0F388E] to-[#1a4fa8] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">التسجيل</h1>
            <div className="w-24 h-1 bg-[#C9A040] mx-auto mb-6"></div>
            <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
              املأ النموذج للتسجيل في جامعة ابن النفيس للعلوم الطبية
            </p>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="shadow-2xl">
            <CardContent className="p-8 md:p-12">
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-[#0F388E] rounded-full flex items-center justify-center mx-auto mb-4">
                  <UserPlus className="w-10 h-10 text-white" />
                </div>
                <h2 className="text-3xl font-bold text-[#0F388E] mb-2">نموذج التسجيل</h2>
                <p className="text-gray-600">يرجى تعبئة جميع الحقول المطلوبة</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <h3 className="text-xl font-bold text-[#0F388E] mb-4 pb-2 border-b-2 border-[#C9A040]">
                    المعلومات الشخصية
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">الاسم الكامل *</label>
                      <Input type="text" name="full_name" value={formData.full_name} onChange={handleChange} required placeholder="الاسم الرباعي" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">البريد الإلكتروني *</label>
                      <Input type="email" name="email" value={formData.email} onChange={handleChange} required placeholder="example@email.com" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">رقم الهاتف *</label>
                      <Input type="tel" name="phone" value={formData.phone} onChange={handleChange} required placeholder="+967 XXX XXX XXX" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">تاريخ الميلاد *</label>
                      <Input type="date" name="birth_date" value={formData.birth_date} onChange={handleChange} required />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">الجنس *</label>
                      <Select value={formData.gender} onValueChange={(value) => handleSelectChange('gender', value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="اختر الجنس" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="ذكر">ذكر</SelectItem>
                          <SelectItem value="أنثى">أنثى</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">الجنسية *</label>
                      <Input type="text" name="nationality" value={formData.nationality} onChange={handleChange} required placeholder="الجنسية" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">المدينة *</label>
                      <Input type="text" name="city" value={formData.city} onChange={handleChange} required placeholder="المدينة" />
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-[#0F388E] mb-4 pb-2 border-b-2 border-[#C9A040]">
                    المعلومات الأكاديمية
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">المؤهل التعليمي *</label>
                      <Select value={formData.qualification} onValueChange={(value) => handleSelectChange('qualification', value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="اختر المؤهل" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="الثانوية العامة">الثانوية العامة</SelectItem>
                          <SelectItem value="بكالوريوس">بكالوريوس</SelectItem>
                          <SelectItem value="دبلوم">دبلوم</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">سنة التخرج *</label>
                      <Input type="text" name="graduation_year" value={formData.graduation_year} onChange={handleChange} required placeholder="2024" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">المعدل/النسبة *</label>
                      <Input type="text" name="gpa" value={formData.gpa} onChange={handleChange} required placeholder="85%" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">اسم المدرسة/الجامعة *</label>
                      <Input type="text" name="school_name" value={formData.school_name} onChange={handleChange} required placeholder="اسم المدرسة" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        الكلية المرغوبة *
                        {formData.desired_college && filteredPrograms.length > 0 && (
                          <span className="text-xs text-green-600 mr-2">
                            ✓ ({filteredPrograms.length} برنامج متاح)
                          </span>
                        )}
                      </label>
                      <Select value={formData.desired_college} onValueChange={(value) => handleSelectChange('desired_college', value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="اختر الكلية" />
                        </SelectTrigger>
                        <SelectContent>
                          {colleges.map((college) => (
                            <SelectItem key={college.id} value={college.name_ar}>
                              {college.name_ar}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">البرنامج المرغوب *</label>
                      <Select 
                        value={formData.desired_program} 
                        onValueChange={(value) => handleSelectChange('desired_program', value)}
                        disabled={!formData.desired_college || filteredPrograms.length === 0}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={
                            !formData.desired_college 
                              ? "اختر الكلية أولاً" 
                              : filteredPrograms.length === 0 
                                ? "لا توجد برامج لهذه الكلية" 
                                : "اختر البرنامج"
                          } />
                        </SelectTrigger>
                        <SelectContent>
                          {filteredPrograms.map((program) => (
                            <SelectItem key={program.id} value={program.name_ar || program.title_ar}>
                              {program.name_ar || program.title_ar}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {formData.desired_college && filteredPrograms.length === 0 && (
                        <p className="text-xs text-amber-600 mt-1">
                          ⚠️ لا توجد برامج متاحة لهذه الكلية حالياً
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-[#0F388E] mb-4 pb-2 border-b-2 border-[#C9A040]">
                    معلومات الاتصال
                  </h3>
                  <div className="grid grid-cols-1 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">العنوان *</label>
                      <Textarea name="address" value={formData.address} onChange={handleChange} required rows={3} placeholder="العنوان بالتفصيل" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">اسم ولي الأمر</label>
                        <Input type="text" name="guardian_name" value={formData.guardian_name} onChange={handleChange} placeholder="اسم ولي الأمر" />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">هاتف ولي الأمر</label>
                        <Input type="tel" name="guardian_phone" value={formData.guardian_phone} onChange={handleChange} placeholder="+967 XXX XXX XXX" />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">صلة القرابة</label>
                        <Select value={formData.guardian_relation} onValueChange={(value) => handleSelectChange('guardian_relation', value)}>
                          <SelectTrigger>
                            <SelectValue placeholder="اختر صلة القرابة" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="والد">والد</SelectItem>
                            <SelectItem value="والدة">والدة</SelectItem>
                            <SelectItem value="أخ">أخ</SelectItem>
                            <SelectItem value="أخت">أخت</SelectItem>
                            <SelectItem value="آخر">آخر</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-4 pb-2 border-b-2 border-[#C9A040]">
                    <div className="flex items-center gap-2">
                      <h3 className="text-xl font-bold text-[#0F388E] dark:text-white">
                        المستندات والوثائق المطلوبة
                      </h3>
                      <span className="text-xs bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 px-2.5 py-0.5 rounded-full font-bold">
                        جميعها اختيارية
                      </span>
                    </div>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      يمكنك إرفاقها الآن أو تسليمها لاحقاً لإدارة القبول
                    </span>
                  </div>

                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-5 leading-relaxed">
                    قم بالضغط على أي نافذة أدناه لاختيار الملف من جهازك أو سحبه مباشرة. الصيغ المدعومة: صور (JPG, PNG, WEBP) أو ملفات (PDF) بحجم أقصى 15 ميجابايت للملف الواحد.
                  </p>

                  {/* 4 Mini Windows Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    {DOCUMENT_TYPES.map((doc) => {
                      const docState = documents[doc.key] || {};
                      const isDragActive = dragOverKey === doc.key;
                      const IconComp = doc.icon;

                      return (
                        <div
                          key={doc.key}
                          className={`rounded-2xl border-2 transition-all p-5 flex flex-col justify-between relative ${
                            docState.success
                              ? 'border-emerald-500/80 bg-emerald-50/20 dark:bg-emerald-950/20 shadow-sm'
                              : isDragActive
                                ? 'border-[#0F388E] dark:border-[#C9A040] bg-blue-50/40 dark:bg-blue-950/20 ring-2 ring-[#0F388E]/20'
                                : 'border-dashed border-gray-300 dark:border-gray-700 bg-white dark:bg-[#0E2243] hover:border-[#0F388E] dark:hover:border-[#C9A040] shadow-sm'
                          }`}
                        >
                          {/* Hidden File Input */}
                          <input
                            type="file"
                            ref={(el) => (fileInputRefs.current[doc.key] = el)}
                            accept={doc.accept}
                            className="hidden"
                            onChange={(e) => {
                              if (e.target.files && e.target.files[0]) {
                                handleFileUpload(doc.key, e.target.files[0]);
                              }
                            }}
                          />

                          {/* Card Header */}
                          <div className="flex items-start justify-between gap-3 mb-3">
                            <div className="flex items-center gap-3">
                              <div className={`w-11 h-11 rounded-xl flex items-center justify-center transition-colors ${
                                docState.success
                                  ? 'bg-emerald-500 text-white'
                                  : 'bg-[#0F388E]/10 dark:bg-white/10 text-[#0F388E] dark:text-[#C9A040]'
                              }`}>
                                <IconComp className="w-5 h-5" />
                              </div>
                              <div>
                                <h4 className="font-bold text-gray-900 dark:text-white text-base leading-snug">
                                  {doc.title}
                                </h4>
                                <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-1">
                                  {doc.subtitle}
                                </p>
                              </div>
                            </div>
                            <span className="text-[11px] font-semibold bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 px-2 py-0.5 rounded-full whitespace-nowrap">
                              اختياري
                            </span>
                          </div>

                          {/* Main Content State */}
                          {docState.uploading ? (
                            /* UPLOADING STATE WITH PROGRESS BAR */
                            <div className="py-4 px-2">
                              <div className="flex items-center justify-between text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">
                                <span className="flex items-center gap-1.5">
                                  <RefreshCw className="w-3.5 h-3.5 animate-spin text-[#0F388E] dark:text-[#C9A040]" />
                                  جاري رفع {doc.title}...
                                </span>
                                <span className="font-mono text-[#0F388E] dark:text-[#C9A040]">{docState.progress}%</span>
                              </div>
                              {/* Progress Bar Container */}
                              <div className="w-full bg-gray-200 dark:bg-gray-700 h-2.5 rounded-full overflow-hidden shadow-inner">
                                <div
                                  className="bg-gradient-to-r from-[#0F388E] to-[#C9A040] h-full rounded-full transition-all duration-300"
                                  style={{ width: `${docState.progress}%` }}
                                />
                              </div>
                              <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-2 truncate text-center">
                                {docState.name} ({docState.size})
                              </p>
                            </div>
                          ) : docState.success ? (
                            /* SUCCESS STATE WITH PREVIEW & CONTROLS */
                            <div className="space-y-3 pt-2">
                              <div className="flex items-center justify-between bg-emerald-500/10 dark:bg-emerald-500/20 border border-emerald-500/30 rounded-xl p-2.5">
                                <div className="flex items-center gap-2 text-xs font-bold text-emerald-700 dark:text-emerald-400">
                                  <CheckCircle2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
                                  <span>تم الرفع بنجاح</span>
                                </div>
                                <span className="text-[11px] text-gray-500 dark:text-gray-400 font-mono">
                                  {docState.size}
                                </span>
                              </div>

                              <div className="flex items-center gap-3 bg-white/70 dark:bg-gray-800/70 p-2.5 rounded-xl border border-gray-100 dark:border-gray-700/60">
                                {docState.preview ? (
                                  <div className="relative w-12 h-12 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 flex-shrink-0 bg-gray-100">
                                    <img
                                      src={docState.preview}
                                      alt={doc.title}
                                      className="w-full h-full object-cover"
                                      onError={(e) => {
                                        e.target.style.display = 'none';
                                      }}
                                    />
                                  </div>
                                ) : (
                                  <div className="w-12 h-12 rounded-lg bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900/40 flex items-center justify-center flex-shrink-0">
                                    <FileText className="w-6 h-6 text-red-500" />
                                  </div>
                                )}
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs font-bold text-gray-800 dark:text-gray-200 truncate" title={docState.name}>
                                    {docState.name}
                                  </p>
                                  <p className="text-[11px] text-gray-500 dark:text-gray-400">
                                    مستند معتمد ومرفق
                                  </p>
                                </div>
                              </div>

                              {/* Action Buttons */}
                              <div className="flex items-center gap-2 pt-1">
                                {docState.url && (
                                  <a
                                    href={docState.url.startsWith('http') ? docState.url : `${BACKEND_URL}${docState.url}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex-1 inline-flex items-center justify-center gap-1.5 py-1.5 px-3 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg text-xs font-semibold transition-colors"
                                  >
                                    <Eye className="w-3.5 h-3.5" />
                                    <span>معاينة</span>
                                  </a>
                                )}
                                <button
                                  type="button"
                                  onClick={() => triggerFileInput(doc.key)}
                                  className="flex-1 inline-flex items-center justify-center gap-1.5 py-1.5 px-3 bg-[#0F388E]/10 hover:bg-[#0F388E]/20 text-[#0F388E] dark:text-[#C9A040] dark:bg-white/5 dark:hover:bg-white/10 rounded-lg text-xs font-semibold transition-colors"
                                >
                                  <RefreshCw className="w-3.5 h-3.5" />
                                  <span>تغيير</span>
                                </button>
                                <button
                                  type="button"
                                  onClick={(e) => handleRemoveFile(doc.key, e)}
                                  className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/40 rounded-lg transition-colors"
                                  title="حذف هذا الملف"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          ) : (
                            /* IDLE / DROP STATE */
                            <div
                              onClick={() => triggerFileInput(doc.key)}
                              onDragOver={(e) => {
                                e.preventDefault();
                                setDragOverKey(doc.key);
                              }}
                              onDragLeave={() => setDragOverKey(null)}
                              onDrop={(e) => {
                                e.preventDefault();
                                setDragOverKey(null);
                                if (e.dataTransfer.files && e.dataTransfer.files[0]) {
                                  handleFileUpload(doc.key, e.dataTransfer.files[0]);
                                }
                              }}
                              className="py-6 px-4 text-center cursor-pointer group/box rounded-xl hover:bg-gray-50/70 dark:hover:bg-gray-800/40 transition-all"
                            >
                              <div className="w-12 h-12 rounded-full bg-gray-100 dark:bg-gray-800 group-hover/box:bg-[#0F388E]/10 dark:group-hover/box:bg-white/10 flex items-center justify-center mx-auto mb-2.5 transition-colors">
                                <Upload className="w-5 h-5 text-gray-400 group-hover/box:text-[#0F388E] dark:group-hover/box:text-[#C9A040] transition-colors" />
                              </div>
                              <p className="text-xs font-bold text-gray-700 dark:text-gray-200 group-hover/box:text-[#0F388E] dark:group-hover/box:text-[#C9A040] transition-colors mb-1">
                                اضغط هنا لتحديد الملف أو اسحبه هنا
                              </p>
                              <p className="text-[11px] text-gray-400 dark:text-gray-500">
                                {doc.acceptText}
                              </p>
                              {docState.error && (
                                <div className="mt-2 text-xs text-red-600 dark:text-red-400 flex items-center justify-center gap-1">
                                  <AlertCircle className="w-3.5 h-3.5" />
                                  <span>{docState.error}</span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="pt-6">
                  <Button 
                    type="submit" 
                    size="lg" 
                    className="w-full bg-[#C9A040] hover:bg-[#C9A040]/90 text-white text-lg"
                    disabled={loading}
                  >
                    {loading ? 'جاري الإرسال...' : 'إرسال طلب التسجيل'}
                    <UserPlus className="mr-2 w-5 h-5" />
                  </Button>
                  <p className="text-center text-sm text-gray-500 mt-4">
                    بالضغط على إرسال، أنت توافق على شروط وأحكام التسجيل
                  </p>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default Register;
